import store from '@/store';
import {
  VuexModule, Module, Mutation, Action, getModule,
} from 'vuex-module-decorators';
import {
  professionItems,
  jobListItems,
  jobListItemsSearch,
  professionStateFilter,
} from '@/api/Profession';
import { JobType } from '@/types/ProfessionType';
import { jobListMap } from '@/api/Map';
import { jobClusterItems } from '@/api/Clusters';

export interface IProfessionState {
  professions: any
  professionTitle: string
  jobs: any
  jobParams: any
  jobFilter: any
  jobPagination: any
  isJobs: boolean
  shortView: string
  jobId: number
  jobSearch: any
  jobType: JobType
  jobFirm: string
  searchJobProfession: any
  searchJobLocation: any
  filterFederalStateItems: any
  jobLocation: any
}

interface IJobs {
  amt: number
  firms?: []
  jobs: []
}

@Module({ dynamic: true, store, name: 'ProfessionModule' })
class Profession extends VuexModule implements IProfessionState {
  professions = {} as any;

  professionTitle = '';

  filterFederalStateItems = null;

  jobs:IJobs = {
    amt: 0,
    firms: [],
    jobs: [],
  } as any;

  jobParams = {
    id: 0,
    jobnews_type: 1,
  } as any;

  jobFilter = {
    dejob: 0,
    recruit: 0,
    tmpjob: 0,
    workjob: 0,
    edujob: 0,
    praktikum: 0,
    fulltime: 0,
    parttime: 0,
    minijob: 0,
    seasonal: 0,
    holiday: 0,
    time_factor: 0,
    specialjob: 0,
    disability_friendly: 0,
    skill_level1: 0,
    skill_level2: 0,
    skill_level3: 0,
    partner_jobs_only: 0,
    other_jobs: 0,
    state_id: 0,
    cluster_id: 0,
  } as any;

  jobPagination = {
    per_page: 20,
    page: 1,
  } as any;

  jobSort = {
    sort: 0,
  } as any;

  jobSearch = {
    profession_id: null,
    location_id: null,
    idcomp: null,
    radius: 0,
    jobnews_type: null,
    fulltext: 0,
    q: '',
    mode: 'AND',
    qmode: 1,
  } as any;

  isJobs = false;

  jobId = 0;

  jobFirm = ''

  jobLocation = ''

  shortView = '';

  jobType = JobType.items;

  searchJobProfession: IProfessionState['searchJobProfession'] = null;

  searchJobLocation: IProfessionState['searchJobLocation'] = null;

  @Mutation
  UPDATE_FILTER_FEDERAL_STATE(payload: any) {
    this.filterFederalStateItems = payload;
  }

  @Mutation
  SET_SEARCH_JOB_PROFESSION(payload: any) {
    this.searchJobProfession = payload;
  }

  @Mutation
  SET_SEARCH_JOB_LOCATION(payload: any) {
    this.searchJobLocation = payload;
  }

  @Mutation
  UPDATE_JOB_SHORT(payload: string) {
    this.shortView = payload;
  }

  @Mutation
  UPDATE_PROFESSION_TITLE(payload: string) {
    this.professionTitle = payload;
  }

  @Mutation
  UPDATE_JOB_TYPE(payload: any) {
    this.jobType = payload;
  }

  @Mutation
  UPDATE_JOB_ID(payload: number) {
    this.jobId = payload;
  }

  @Mutation
  UPDATE_JOB_FIRM(payload: string) {
    this.jobFirm = payload;
  }

  @Mutation
  UPDATE_JOB_LOCATION(payload: string) {
    this.jobLocation = payload;
  }

  @Mutation
  UPDATE_SHOW_JOBS(payload: boolean) {
    this.isJobs = payload;
  }

  @Mutation
  UPDATE_PAGINATION(payload: any) {
    const { obj, value } = payload;
    this.jobPagination[obj] = value;
  }

  @Mutation
  UPDATE_SEARCH(payload: any) {
    const { obj, value } = payload;
    this.jobSearch[obj] = value;
  }

  @Mutation
  UPDATE_FILTER(payload: any) {
    const { obj, value } = payload;
    this.jobFilter[obj] = value;
  }

  @Mutation
  UPDATE_SORT(payload: any) {
    const { obj, value } = payload;
    this.jobSort[obj] = value;
  }

  @Mutation
  UPDATE_PROFESSION(payload: any) {
    this.professions = payload;
  }

  @Mutation
  UPDATE_JOBS(payload: any) {
    this.jobs = payload;
  }

  @Mutation
  UPDATE_PROFESSION_ID(payload: any) {
    this.jobParams.id = payload;
  }

  @Mutation
  SET_FILTER_DEFAULT() {
    Object.keys(this.jobFilter).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(this.jobFilter, key)) {
        this.jobFilter[key] = 0;
      }
    });
  }

  @Mutation
  SET_PARAMS_DEFAULT() {
    this.jobPagination.page = 1;
    this.jobPagination.per_page = 20;
    this.jobSort.sort = 0;
    this.jobParams.id = 0;
    this.jobParams.jobnews_type = 1;
  }

  @Mutation
  SET_SEARCH_DEFAULT() {
    this.jobSearch.radius = 0;
    this.jobSearch.jobnews_type = null;
    this.jobSearch.fulltext = 0;
    this.jobSearch.q = '';
    this.jobSearch.mode = 'AND';
    this.jobSearch.qmode = 1;
  }

  @Mutation
  SET_QUERY_SEARCH_DEFAULT() {
    this.searchJobProfession = null;
    this.searchJobLocation = null;
    this.jobSearch.profession_id = null;
    this.jobSearch.location_id = null;
    this.jobSearch.idcomp = null;
  }

  @Action
  async SetSearchJobProfession(payload: any) {
    this.SET_SEARCH_JOB_PROFESSION(payload);
  }

  @Action
  async SetSearchJobLocation(payload: any) {
    this.SET_SEARCH_JOB_LOCATION(payload);
  }

  @Action({ rawError: true })
  async GetProfession(payload: any) {
    const request = await professionItems(payload);
    this.UPDATE_PROFESSION(request);
  }

  @Action({ rawError: true })
  async GetJobs(payload: any) {
    const params = {
      ...payload,
      ...this.jobSort,
      ...this.jobParams,
      ...this.jobFilter,
      ...this.jobPagination,
    };
    const request = await jobListItems(params);
    this.UPDATE_JOBS(request);
  }

  @Action({ rawError: true })
  async GetJobsSearch(payload: any) {
    const params = {
      ...payload,
      ...this.jobSort,
      ...this.jobSearch,
      ...this.jobParams,
      ...this.jobFilter,
      ...this.jobPagination,
    };
    const request = await jobListItemsSearch(params);
    this.UPDATE_JOBS(request);
  }

  @Action({ rawError: true })
  async GetJobsMap(payload: any) {
    const params = {
      ...payload.params,
      ...this.jobSort,
      ...this.jobParams,
      ...this.jobFilter,
      ...this.jobPagination,
    };
    const request = await jobListMap(params, {
      job_ids: payload.ids,
    });
    this.UPDATE_JOBS(request);
  }

  @Action
  async SetProfessionId(payload: any) {
    this.UPDATE_PROFESSION_ID(payload);
  }

  @Action
  async SetPagination(payload: any) {
    this.UPDATE_PAGINATION(payload);
  }

  @Action
  async SetSort(payload: any) {
    this.UPDATE_SORT(payload);
  }

  @Action
  async SetVisibleJobs(payload: boolean) {
    this.UPDATE_SHOW_JOBS(payload);
  }

  @Action
  async SetJobId(payload: number) {
    this.UPDATE_JOB_ID(payload);
  }

  @Action
  async SetJobFirm(payload: string) {
    this.UPDATE_JOB_FIRM(payload);
  }

  @Action
  async SetJobLocation(payload: string) {
    this.UPDATE_JOB_LOCATION(payload);
  }

  @Action
  async SetJobShort(payload: string) {
    this.UPDATE_JOB_SHORT(payload);
  }

  @Action
  async SetSearch(payload: any) {
    this.UPDATE_SEARCH(payload);
  }

  @Action
  async SetFilter(payload: any) {
    this.UPDATE_FILTER(payload);
  }

  @Action
  async SetJobType(payload: any) {
    this.UPDATE_JOBS({});
    this.UPDATE_JOB_TYPE(payload);
  }

  @Action
  async SetProfessionTitle(payload: string) {
    this.UPDATE_PROFESSION_TITLE(payload);
  }

  @Action({ rawError: true })
  async GetFederalStateItems(payload: any) {
    const params = {
      ...payload,
    };
    const request = await professionStateFilter(params);
    this.UPDATE_FILTER_FEDERAL_STATE(request);
  }

  @Action
  async SetParamsDefault() {
    this.SET_FILTER_DEFAULT();
    this.SET_SEARCH_DEFAULT();
    this.SET_PARAMS_DEFAULT();
    this.SET_QUERY_SEARCH_DEFAULT();
  }
}

export const ProfessionModule = getModule(Profession);
